import { css } from '@emotion/css';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom-v5-compat';

import { type CoreApp, type GrafanaTheme2 } from '@grafana/data';
import { t } from '@grafana/i18n';
import { locationService } from '@grafana/runtime';
import { type DataQuery } from '@grafana/schema';
import { Alert, Button, Stack, useStyles2 } from '@grafana/ui';
import { useQueryLibraryContext } from 'app/features/explore/QueryLibrary/QueryLibraryContext';

import { QueryLibraryInteractions } from './QueryLibraryAnalyticsEvents';
import { EXPLORE_NEW_SAVED_QUERY_CONTEXT, type SavedQuerySaveAction } from './SavedQueries/SavedQueriesDetailsPanel';
import { EditQueryDrawer } from './SavedQueries/SavedQueriesPage/EditQueryDrawer';
import { selectors } from './e2e-selectors/selectors';
import { useQueryLibrarySave } from './hooks/useQueryLibrarySave';
import { SAVED_QUERIES_BASE_URL } from './routes';
import { useGetNewSavedQuery } from './utils/dataFetching';
import { getSafeSavedQueryReturnTo, getSavedQueryOrigin } from './utils/savedQueryReturnTo';

import { getQueryLibraryRenderContext } from './index';

interface QueryLibraryEditingHeaderProps {
  query: DataQuery;
  app?: CoreApp;
  editSavedQueryRef?: string;
  /** 'edit' updates an existing saved query (default); 'add' composes and saves a brand-new one. */
  mode?: 'edit' | 'add';
  onCancelEdit?: () => void;
  onUpdateSuccess?: () => void;
  onSelectQuery?: (query: DataQuery) => void;
}

export function QueryLibraryEditingHeader({
  query,
  app,
  editSavedQueryRef,
  mode = 'edit',
  onCancelEdit,
  onUpdateSuccess,
  onSelectQuery,
}: QueryLibraryEditingHeaderProps) {
  const styles = useStyles2(getStyles);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateQuery, isEnabled } = useQueryLibrarySave();
  const { setContext, openDrawer, triggerAnalyticsEvent } = useQueryLibraryContext();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const isAdd = mode === 'add'; // Never navigate to the raw query param — crafted Explore links could redirect elsewhere.
  const savedQueryReturnTo = getSafeSavedQueryReturnTo(searchParams.get('savedQueryReturnTo'));
  // Which surface sent the user to Explore. Reported alongside the return-leg events, which would
  // otherwise only say `app: 'explore'` and lose the entry point entirely.
  const origin = getSavedQueryOrigin(savedQueryReturnTo);

  // Seed ambient context for the drawer/analytics while the banner is mounted; restore the host-app
  // context on unmount so Save-and-continue (and discard) don't leave explore-new-saved-query stuck.
  // Analytics convention for the two Explore round trips this banner drives:
  //   add mode  → `app: 'explore-new-saved-query'`, a context of its own, inherited by every event the
  //               save drawer fires, so the whole compose flow is identifiable without extra properties.
  //   edit mode → `app: 'explore'` (the surface the save happens on) plus `origin`, because the edit
  //               entry point varies (list page / detail page / in-Explore drawer) and the context alone
  //               cannot tell them apart.
  // Events fired at mount must pass the context explicitly — see the banner-shown effect below.
  useEffect(() => {
    setContext(isAdd ? EXPLORE_NEW_SAVED_QUERY_CONTEXT : getQueryLibraryRenderContext(app));
    return () => {
      setContext(getQueryLibraryRenderContext(app));
    };
  }, [isAdd, app, setContext]);

  const {
    data: newSavedQuery,
    isLoading: isNewQueryLoading,
    isError: isNewQueryError,
  } = useGetNewSavedQuery(isAdd ? query : undefined);

  const canOpenSaveDrawer = Boolean(newSavedQuery) && !isNewQueryLoading && !isNewQueryError;

  // Entering add mode. Declared above the early return below so hook order stays stable, and guarded by
  // a ref so re-renders don't re-report. The context is passed explicitly rather than relying on the
  // effect above: on first mount that setContext hasn't flushed, so the ambient value is still the
  // previous surface — and the ref means the event is never retried once the provider rerenders.
  const hasReportedBanner = useRef(false);
  useEffect(() => {
    if (!isAdd || !isEnabled() || hasReportedBanner.current) {
      return;
    }
    hasReportedBanner.current = true;
    triggerAnalyticsEvent(
      QueryLibraryInteractions.newSavedQueryBannerShown,
      {
        datasourceType: query.datasource?.type,
      },
      EXPLORE_NEW_SAVED_QUERY_CONTEXT
    );
  }, [isAdd, isEnabled, query.datasource?.type, triggerAnalyticsEvent]);

  const reopenLibraryDrawer = () => {
    openDrawer({
      datasourceFilters: [],
      onSelectQuery,
      options: { context: app ?? 'unknown', highlightQuery: editSavedQueryRef },
    });
  };

  const handleSave = () => {
    if (!editSavedQueryRef) {
      return;
    }

    const onSuccess = () => {
      onUpdateSuccess?.();

      if (savedQueryReturnTo) {
        return navigate(savedQueryReturnTo);
      } else {
        reopenLibraryDrawer();
      }
    };

    updateQuery(query, { editSavedQueryRef, origin }, onSuccess);
  };

  const handleDiscard = () => {
    triggerAnalyticsEvent(QueryLibraryInteractions.updateQueryFromExploreCancelled, {
      datasourceType: query.datasource?.type,
      origin,
    });
    onCancelEdit?.();
    if (savedQueryReturnTo) {
      navigate(savedQueryReturnTo);
      return;
    }
    reopenLibraryDrawer();
  };

  // Edit mode needs an editSavedQueryRef to know which saved query to update; add mode has none yet.
  if (!isEnabled() || (!isAdd && editSavedQueryRef === undefined)) {
    return null;
  }

  if (isAdd) {
    // Clear Redux add-mode and strip ?createSavedQuery so browser Back / refresh can't re-enter it.
    const exitAddMode = () => {
      onUpdateSuccess?.();
      locationService.partial({ createSavedQuery: null }, true);
    };

    const handleOpenSaveDrawer = () => {
      triggerAnalyticsEvent(
        QueryLibraryInteractions.saveQueryToLibraryClicked,
        {
          datasourceType: query.datasource?.type,
        },
        EXPLORE_NEW_SAVED_QUERY_CONTEXT
      );
      setIsDrawerOpen(true);
    };

    const handleDiscardAdd = () => {
      triggerAnalyticsEvent(
        QueryLibraryInteractions.newSavedQueryFromExploreCancelled,
        {
          datasourceType: query.datasource?.type,
        },
        EXPLORE_NEW_SAVED_QUERY_CONTEXT
      );
      onCancelEdit?.();
      locationService.partial({ createSavedQuery: null }, true);
      navigate(SAVED_QUERIES_BASE_URL);
    };
    const saveActions: SavedQuerySaveAction[] = [
      {
        key: 'save',
        label: t('explore.query-library.save', 'Save'),
        // Land on the newly created query's detail page.
        onAfterSave: (uid) => {
          exitAddMode();
          navigate(`${SAVED_QUERIES_BASE_URL}/${uid}`);
        },
      },
      {
        key: 'save-and-view-all',
        label: t('explore.query-library.save-and-view-all', 'Save and view all'),
        icon: 'list-ul',
        testId: selectors.components.savedQueriesModal.saveAndViewAll,
        onAfterSave: () => {
          exitAddMode();
          navigate(SAVED_QUERIES_BASE_URL);
        },
      },
      {
        key: 'save-and-continue',
        label: t('explore.query-library.save-and-continue', 'Save and continue in Explore'),
        icon: 'compass',
        testId: selectors.components.savedQueriesModal.saveAndContinue,
        // Stay in Explore with the query still in the editor: close the drawer and exit add mode.
        onAfterSave: () => {
          setIsDrawerOpen(false);
          exitAddMode();
        },
      },
    ];

    return (
      <>
        <div className={styles.header}>
          <div className={styles.headerText}>{t('explore.query-library.adding-title', 'Adding a new saved query')}</div>
          <Stack direction="row" gap={1} wrap="nowrap">
            <Button
              variant="secondary"
              size="sm"
              fill="text"
              onClick={handleDiscardAdd}
              className={styles.discardButton}
            >
              {t('explore.query-library.discard', 'Discard')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              fill="outline"
              icon={isNewQueryLoading ? 'spinner' : undefined}
              disabled={!canOpenSaveDrawer}
              onClick={handleOpenSaveDrawer}
              className={styles.saveButton}
            >
              {t('explore.query-library.save-query', 'Save query')}
            </Button>
          </Stack>
        </div>
        {isNewQueryError ? (
          <Alert
            severity="error"
            title={t(
              'explore.query-library.add-prepare-error',
              'Failed to prepare the new saved query. Check the datasource and try again.'
            )}
          />
        ) : null}
        {isDrawerOpen && newSavedQuery ? (
          <EditQueryDrawer query={newSavedQuery} saveActions={saveActions} onClose={() => setIsDrawerOpen(false)} />
        ) : null}
      </>
    );
  }

  return (
    <div className={styles.header}>
      <div className={styles.headerText}>{t('explore.query-library.editing-title', 'Editing from saved queries')}</div>
      <Stack direction="row" gap={1} wrap="nowrap">
        <Button variant="secondary" size="sm" fill="text" onClick={handleDiscard} className={styles.discardButton}>
          {t('explore.query-library.discard', 'Discard')}
        </Button>
        <Button variant="primary" size="sm" fill="outline" onClick={handleSave} className={styles.saveButton}>
          {t('explore.query-library.save-return', 'Save and return to saved queries')}
        </Button>
      </Stack>
    </div>
  );
}

const getStyles = (theme: GrafanaTheme2) => {
  const contrastColor = theme.colors.primary.contrastText;
  const hoverBackgroundColor = theme.colors.primary.shade;

  return {
    header: css({
      backgroundColor: theme.colors.primary.main,
      color: contrastColor,
      padding: theme.spacing(1.5, 2),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '44px',
      borderTopLeftRadius: theme.shape.radius.default,
      borderTopRightRadius: theme.shape.radius.default,
      borderBottomLeftRadius: 'unset',
      borderBottomRightRadius: 'unset',

      [theme.breakpoints.down('md')]: {
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.75, 1),
        minHeight: 'auto',
      },
    }),
    headerText: css({
      color: contrastColor,
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: theme.typography.body.fontSize,

      [theme.breakpoints.down('md')]: {
        fontSize: theme.typography.bodySmall.fontSize,
        flex: 1,
      },
    }),
    discardButton: css({
      color: contrastColor,
      '&:hover': {
        color: contrastColor,
        backgroundColor: hoverBackgroundColor,
      },
    }),
    saveButton: css({
      color: contrastColor,
      borderColor: contrastColor,
      '&:hover': {
        color: contrastColor,
        borderColor: contrastColor,
        backgroundColor: hoverBackgroundColor,
      },
    }),
  };
};
